/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { availableFlowEnvironments } from '../../../apollo/reactiveVariables/FlowEnvironmentVariables';
import errorImage from '../../../assets/error-generic.png';
import { Constants } from '../../../common/Constants';
import { Paths } from '../../../common/enums/Paths';
import { EntryPoint, LogCategory, PanelRegion, PanelType, ScenarioName } from '../../../logger';
import { FileAttachment } from '../../../models/fileService/FileAttachment';
import { getApprovalFileService } from '../../../service/file-upload/ApprovalsFileService';
import {
    ApprovalType,
    ApprovalItem,
    ApprovalResponseType,
    CreateAddinApprovalControllerInput,
    CreateApprovalControllerOutput,
    CreateApprovalClient,
    ApprovalCreator,
    ApprovalPolicyType,
    ApprovalStepSettingInput,
    ODSPFileClient,
    ODSPShareLinkOption,
    Permission,
    ShareLinkScope,
    ShareLinkType,
    TeamsEnvironment,
} from '../../../service/gen';
import { ApprovalTypeToString } from '../../../utilities/ApprovalTypeHelper';
import { newGuid } from '../../../utilities/AppUtils';
import { getFileNameFromURL } from '../../../utilities/FileUtils';
import { createApprovalsPermissionId } from '../../../utilities/OfficeAddinDocumentUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getSelectionText, insertComment } from '../../../utilities/WordAddinUtils';
import { PeopleItem } from '../../common/StepApproversPicker/PeoplePickerList.component';

import { ErrorPage } from '../ErrorPage/ErrorPage';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import { CreatePageState } from './CreateAddinPageState';

import { CreateApprovalComponent, CreateApprovalData } from './CreateApproval.component';

declare const MIDDLE_TIER_ENDPOINT: string;

export interface CreateAddinApprovalProps {
    // Current selection
    entryPoint: EntryPoint;

    // Partner Id.
    documentPid?: string;
}

export const CreateAddinApprovalContainer: React.FC<CreateAddinApprovalProps> = (
    props: CreateAddinApprovalProps
): JSX.Element => {
    const { entryPoint } = props;

    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();
    const logCategory = LogCategory.CreateApprovalView;

    const { approval_create_loading_text, addin_create_share_link_Failed } = loc.strings;

    const [documentPid, setDocumentPid] = React.useState<string>(props.documentPid);
    const [files, setFiles] = React.useState<FileAttachment[]>([]);

    const [pageState, setPageState] = React.useState<CreatePageState>(CreatePageState.Normal);
    const onRetry = (): void => setPageState(CreatePageState.Normal);

    const history = useHistory();
    const onBack = (): void => {
        logger.logTraceInfo(LogCategory.CreateApprovalView, 'Navigating back to the template list page');

        history.goBack();
    };

    React.useEffect((): void => {
        logger.logTraceInfo(logCategory, `Loaded create approval form. Entry Point: ${entryPoint}`);
    }, [logger, entryPoint, logCategory]);

    const environmentList = useReactiveVar<TeamsEnvironment[]>(availableFlowEnvironments);
    const currentEnvironment = environmentList
        .filter((env: TeamsEnvironment): boolean => env.Properties.IsDefault)
        .shift();

    const documentURL = Office.context.document.url || '';
    const isLocalFile = !documentURL.startsWith('https://');

    const page = isLocalFile ? (
        <ErrorPage message={loc.strings.addin_upload_local_file} image={errorImage} />
    ) : pageState === CreatePageState.Loading ? (
        <LoadingPage message={approval_create_loading_text} />
    ) : pageState === CreatePageState.ItemNotFoundError ? (
        <ErrorPage
            message={loc.strings.addin_document_not_found}
            additionalMessage={loc.strings.addin_document_not_found_additionalMessage}
            image={errorImage}
            onAction={onBack}
        />
    ) : pageState === CreatePageState.GeneralError ? (
        <ErrorPage image={errorImage} onAction={onRetry} />
    ) : null;

    const infoPage = page ? (
        <div className='topContainer modal-container desktop'>
            <div className='approval-scrollable-content'>{page}</div>
        </div>
    ) : null;

    // Get the selected section on context menu entry point
    const [selectedText, setSelectedText] = React.useState<string>('');
    React.useEffect((): void => {
        if (entryPoint === EntryPoint.OfficeAddinCommand) {
            getSelectionText().then((text: string): void => setSelectedText(text));
        }
    }, [entryPoint]);

    React.useEffect((): void => {
        const fileName = getFileNameFromURL(documentURL);
        const hostFile: FileAttachment = {
            id: 0,
            name: fileName,
            status: UploadStatus.PROGRESS,
            progress: 10,
        };

        const setProgress = (progress: number): void => {
            setFiles([{ ...hostFile, progress }]);
        };

        const getDocumentPermission = async (): Promise<Permission> => {
            const odspController = new ODSPFileClient();
            const item = await odspController.getDriveItem(newGuid(), documentURL);
            setProgress(45);

            if (!item) {
                setPageState(CreatePageState.ItemNotFoundError);
                return null;
            }

            const documentId = item.Id;
            const driveId = item.RemoteItem?.ParentReference?.DriveId ?? item.ParentReference?.DriveId;
            const option: ODSPShareLinkOption = {
                Type: ShareLinkType[ShareLinkType.Review],
                Scope: ShareLinkScope[ShareLinkScope.Organization],
            };

            const permission = await odspController.createShareLinkWithId(driveId, documentId, newGuid(), option);
            setProgress(85);

            if (!documentPid) {
                const pid = await createApprovalsPermissionId(documentId, driveId, permission.Id);
                setDocumentPid(pid);
            }

            setProgress(100);
            return permission;
        };

        setFiles([hostFile]);
        getDocumentPermission()
            .then((permission: Permission): void => {
                const file: FileAttachment = {
                    name: fileName,
                    id: 0,
                    status: UploadStatus.COMPLETE,
                    shareableUrl: permission.Link.WebUrl,
                    description: permission.Link.WebUrl,
                };
                setFiles([file]);
            })
            .catch((error: any): void => {
                logger.logTraceHttpException('createShareLink', error.message, error.status);

                const file: FileAttachment = {
                    name: fileName,
                    id: 0,
                    status: UploadStatus.FAILED,
                    description: addin_create_share_link_Failed,
                };
                setFiles([file]);
            });
    }, [addin_create_share_link_Failed, documentURL, logger, documentPid]);

    /**
     * called when send button is pressed
     */
    const onNext = ({
        title,
        details,
        approvers,
        allMustRespond,
        isCustomResponsesEnabled: isCustomResponse,
        environmentTarget,
        customResponses: responseOptions,
        priority,
        files,
        stepApprovers,
    }: CreateApprovalData): void => {
        const clientRequestGUID = newGuid();
        setPageState(CreatePageState.Loading);

        const titleString = title;
        const detailsString = details;
        const approversList = approvers;
        const approvalResponseType =
            stepApprovers?.length > 0 && !approvers.length
                ? ApprovalResponseType.Sequential
                : isCustomResponse
                ? allMustRespond
                    ? ApprovalResponseType.CustomResponseAwaitAll
                    : ApprovalResponseType.CustomResponse
                : allMustRespond
                ? ApprovalResponseType.BasicAwaitAll
                : ApprovalResponseType.Basic;
        const approvalType = ApprovalType.Basic;

        // remove ResponseOption from api input for non-CustomResponse requests.
        const responseOptionsInput = isCustomResponse ? responseOptions : undefined;

        logger.logTraceInfo(
            logCategory,
            `Clicked on create approval submit, approval type ${approvalType}, approval response type ${approvalResponseType}`
        );

        // Item link for uploaded file
        const completedFiles =
            files &&
            files.filter(
                (file: FileAttachment): boolean => file.status === UploadStatus.COMPLETE && !!file.shareableUrl
            );

        const item: ApprovalItem =
            completedFiles && completedFiles.length > 0
                ? {
                      Link: completedFiles[0].shareableUrl,
                      LinkDescription: completedFiles[0].name,
                  }
                : null;

        const itemType = item && completedFiles[0].type;
        const itemSize = item ? completedFiles[0].size : 0;

        const approverNamesText = new Array<string>();
        const approversUserObjectId = new Array<string>();

        approversList.forEach((person: IPersonaProps): void => {
            approverNamesText.push(person.text);
        });

        approversList.forEach((person: IPersonaProps): void => {
            approversUserObjectId.push(person.id);
        });

        logger.logCreateApproval({
            titleSize: title.length,
            numberOfApprovers: approvers.length,
            detailsSize: details.length,
            allMustApprove: allMustRespond ? 'True' : 'False',
            clientRequestId: clientRequestGUID,
            correlationId: clientRequestGUID,
            hasItem: !!item,
            itemType,
            itemSize,
            approvalResponseType,
            priority,
            customResponseOptionCount: responseOptions ? responseOptions.length : undefined,
            customResponseOptionLengths: responseOptions
                ? responseOptions.map((option: string): number => option.length).join(',')
                : undefined,
        });

        const data: CreateAddinApprovalControllerInput = {
            DocumentPermissionId: documentPid,
            CallbackUrls: [MIDDLE_TIER_ENDPOINT + Constants.WEBHOOK_ENDPOINT],
            Creator: ApprovalCreator.WordAddIn,
            Source: host.getAppSource(),
            AssignedTo: approversUserObjectId,
            Type: approvalResponseType,
            ClientRequestId: clientRequestGUID,
            Title: titleString.trim(),
            Details: detailsString.trim(),
            Item: item,
            Priority: priority,
            ResponseOptions: responseOptionsInput,
            Steps: stepApprovers?.map(
                (a: PeopleItem): ApprovalStepSettingInput => ({
                    AssignedTo: a.personaList?.map((person: IPersonaProps): string => person.id),
                    ApprovalPolicy: ApprovalPolicyType.Any, // current default is any
                })
            ),
            ...(environmentTarget && { FlowEnvironment: environmentTarget }),
            ApprovalType: ApprovalType.Basic,
        };
        logger.logTraceInfo(logCategory, 'Calling Approvals create endpoint from personal app');
        new CreateApprovalClient()
            .createAddinApproval(data, clientRequestGUID)
            .then((input: CreateApprovalControllerOutput): void => {
                logger.findScenario(ScenarioName.CreateApprovals).stop();

                if (entryPoint === EntryPoint.OfficeAddinCommand) {
                    insertComment(loc.strings.addin_approvals_created_on_selection);
                }

                history.push(Paths.OfficeBasePath + Paths.OfficeTaskPane + Paths.OfficeViewDetails, {
                    approvalId: input.ApprovalId,
                    approvalType: ApprovalTypeToString(approvalType),
                    flowEnvironment: environmentTarget,
                    entryPoint,
                    documentPid,
                });
            })
            .catch((error: any): void => {
                setPageState(CreatePageState.GeneralError);
                logger.logTraceHttpException('CreateApproval', error.message, error.status);
            });
    };

    const fileName = getFileNameFromURL(documentURL);

    const hideMainContent = !!page;
    const createApprovalPage = (
        <div className={'topContainer modal-container desktop with-footer'} hidden={hideMainContent}>
            <div role='tabpanel' className='approval-scrollable-content'>
                <CreateApprovalComponent
                    {...props}
                    title={fileName}
                    details={selectedText}
                    entryPoint={entryPoint}
                    currentEnvironment={currentEnvironment?.Name}
                    isOfficeAddin={true}
                    documentPid={documentPid}
                    onNext={onNext}
                    isFileUploadEnabled
                    fileService={getApprovalFileService()}
                    files={files}
                    isCreateDiffEnvironmentEnabled={false}
                    onBack={onBack}
                />
            </div>
        </div>
    );
    const createRequest = (
        <>
            {infoPage}
            {createApprovalPage}
        </>
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    logger.useCreateScenarioEffect(
        ScenarioName.CreateApprovals,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        getAppHost().getPersonalAppEntryPoint(),
        entryPoint,
        PanelRegion.OfficeTaskPane,
        PanelType.WordAddin
    );

    return createRequest;
};
